import { requestStyleclipProfileHide } from '@apis/renewal/styleclip';
import Products from '@components/common/renewal/Products';
import { useQueryClient } from '@tanstack/react-query';
import { Product } from '@wconcept/wconcept-common/models/renewal/product';
import {
  addAsIsMWebDomain,
  addAsIsPcWebDomain,
  currentUrl,
} from '@wconcept/wconcept-common/utils/domainUtil';
import { isNativeAppBoth } from '@wconcept/wconcept-common/utils/userAgentUtil';
import ShareSnsBtn from '@wconcept/wconcept-ui-renewal/src/components/molecules/_btn/ShareSnsBtn';
import PrdcUnitItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/PrdcUnitItem';
import AgreeModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/AgreeModal';
import MarketingAgreeModal, {
  type MarketingAgreeResult,
} from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/MarketingAgreeModal';
import CollectionOptionModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/CollectionOptionModal';
import FoldMenuModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/FoldMenuModal';
import LoadingModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/LoadingModal';
import RelatedPrdModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/RelatedPrdModal';
import ShareModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/ShareModal';
import StylingGuideModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/StylingGuideModal';
import { ModalsContext } from '@wconcept/wconcept-ui-renewal/src/contexts/ModalsContext';
import NoticeModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/NoticeModal';
import {
  useCallback,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useRelatedPrdModalStore } from '@store/modalStore';
import { useSearchParams } from 'next/navigation';
import FollowListModal from '@wconcept/wconcept-ui-renewal/src/components/organisms/_modal/FollowListModal';
import { FollowUserInfo } from '@apis/renewal/follow';
import useGlobalState from './useGlobalState';
import useHistoryRouter from './useLinkTo';
import useMyHeartItem from './useMyHeartItem';
import useRenderProducts from './useRenderProducts';
import useShareSns from './useShareSns';

export type ShareSnsType = 'kakao' | 'facebook' | 'url';

const DEFAULT_SHARE_TYPES: ShareSnsType[] = ['kakao', 'facebook', 'url'];

interface UseModalManagerProps {
  doAfterAddMyHeart?: (product: Product, index?: number) => void;
  doAfterDeleteMyHeart?: (product: Product, index?: number) => void;
}

const useModalManager = ({
  doAfterAddMyHeart = () => {},
  doAfterDeleteMyHeart = () => {},
}: UseModalManagerProps = {}) => {
  const { openModal, closeModal } = useContext(ModalsContext);
  const { onUrlClipboard, shareFacebook, shareKakao } = useShareSns();
  const { linkTo } = useHistoryRouter();
  const queryClient = useQueryClient();
  const {
    myHeartList,
    appInfo,
    assistData,
    deviceInfo,
    addMyHeartProductItem,
    deleteMyHeartProdcutItem,
    isMobile,
  } = useGlobalState();

  const { renderProducts } = useRenderProducts({
    doAfterAddMyHeart: (product, index) => {
      doAfterAddMyHeart?.(product, index);
      addMyHeartProductItem(product.itemCd);
    },
    doAfterDeleteMyHeart: (product, index) => {
      doAfterDeleteMyHeart?.(product, index);
      deleteMyHeartProdcutItem(product.itemCd);
    },
  });
  const { setRelatedPrdModalState, clearRelatedPrdModalState } =
    useRelatedPrdModalStore();

  const { onClickHeart: onClickProductHeart } = useMyHeartItem({
    isLogin: !!assistData?.isLogin,
    doAfterAddMyHeart: param => {
      addMyHeartProductItem(param.itemCd);
    },
    doAfterDeleteMyHeart: param => {
      deleteMyHeartProdcutItem(param.itemCd);
    },
  });

  const openLoginConfirm = useCallback(
    async (targetUrl?: string, notConfirm?: boolean, text?: string) => {
      const confirmText =
        text ||
        '로그인이 필요한 서비스 입니다.\n로그인 페이지로 이동하시겠습니까?';

      if (notConfirm) {
        const url = `${await addAsIsMWebDomain(
          '/Member/Login'
        )}?rUrl=${encodeURIComponent(targetUrl || currentUrl())}`;
        linkTo.push({
          url,
        });
      } else if (window.confirm(confirmText)) {
        const url = `${await addAsIsMWebDomain(
          '/Member/Login'
        )}?rUrl=${encodeURIComponent(targetUrl || currentUrl())}`;
        linkTo.push({
          url,
        });
      }
      return false;
    },
    [linkTo]
  );

  const openFoldMenuModal = ({
    value,
    onSubmit,
  }: {
    value: {
      title: string;
      cateMenuList: { value: number; text: string }[];
      selectedValue: string;
      isDesktop: boolean;
    };
    onSubmit: (value: string) => void;
  }) => {
    openModal(FoldMenuModal, {
      type: !isMobile ? 'modal' : 'drawer',
      value,
      onSubmit,
    });
  };
  // title: '공유하기',
  // text: eventTitle,
  const getIsGnbType = useCallback(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('gnbType') === 'Y';
  }, []);

  const openShareModal = ({
    title,
    targetUrl,
    onClickShare,
    suffixText,
    shareTypes = DEFAULT_SHARE_TYPES,
    manageNativeActionBar = false,
    onCopyToast,
  }: {
    title: string;
    targetUrl?: string;
    onClickShare?: (text: string, title?: string) => void;
    suffixText?: string;
    shareTypes?: ShareSnsType[];
    manageNativeActionBar?: boolean;
    onCopyToast?: (message: string) => void;
  }) => {
    const shouldManageActionBar =
      manageNativeActionBar && appInfo?.isNativeAppBoth && getIsGnbType();

    if (shouldManageActionBar) {
      document.location.href = 'wconceptkr://actionBarOff';
    }

    const restoreActionBar = () => {
      if (shouldManageActionBar) {
        document.location.href = 'wconceptkr://actionBarOn';
      }
    };

    const snsButtonMap: Record<ShareSnsType, ReactElement> = {
      kakao: (
        <ShareSnsBtn
          key="kakao"
          type="kakao"
          onClick={() => {
            onClickShare?.('kakao');
            shareKakao(title, !!appInfo?.isNativeAppBoth, !isMobile, {
              targetUrl: targetUrl || '',
            });
          }}
        />
      ),
      facebook: (
        <ShareSnsBtn
          key="facebook"
          type="facebook"
          onClick={() => {
            onClickShare?.('facebook');
            shareFacebook(!!appInfo?.isNativeAppBoth, !isMobile, {
              targetUrl: targetUrl || '',
            });
          }}
        />
      ),
      url: (
        <ShareSnsBtn
          key="url"
          type="url"
          onClick={() => {
            onClickShare?.('url');
            onUrlClipboard({
              isMobile: !!isMobile,
              device: deviceInfo?.device || '',
              isNativeAppBoth: !!appInfo?.isNativeAppBoth,
              targetUrl,
              onCopyToast,
            });
          }}
        />
      ),
    };

    openModal(ShareModal, {
      type: !isMobile ? 'modal' : 'drawer',
      onClose: restoreActionBar,
      value: {
        constText: title,
        suffixText,
        snsItems: shareTypes.map(type => snsButtonMap[type]),
        isDesktop: !isMobile,
        isIPhoneXApp: appInfo?.isIPhoneXApp,
      },
    });
  };

  const openNoticeModal = ({
    title,
    message,
    onClose,
    onSubmit,
    type = 'alert',
    submitText,
    closeText,
  }: {
    title?: string;
    message: string;
    onClose?: () => void;
    onSubmit?: () => void;
    type?: 'alert' | 'confirm';
    isMobile: boolean;
    submitText?: string;
    closeText?: string;
  }) =>
    new Promise((resolve, reject) => {
      if (appInfo?.isNativeAppBoth && getIsGnbType()) {
        document.location.href = 'wconceptkr://actionBarOff';
      }
      openModal(NoticeModal, {
        type: !isMobile ? 'modal' : 'drawer',
        mode: 'mainBottom',
        desktopMode: 'center',
        onClose: () => {
          if (appInfo?.isNativeAppBoth && getIsGnbType()) {
            document.location.href = 'wconceptkr://actionBarOn';
          }
          onClose?.();
          resolve(false);
        },
        onSubmit: () => {
          if (appInfo?.isNativeAppBoth && getIsGnbType()) {
            document.location.href = 'wconceptkr://actionBarOn';
          }
          onSubmit?.();
          resolve(true);
        },
        value: {
          text: message,
          btnType: type === 'alert' ? 'single' : 'double',
          submitText: submitText || '확인',
          closeText: closeText || '취소',
          isIPhoneXApp: appInfo.isIPhoneXApp,
          isDesktop: !isMobile,
        },
        className: 'notice-modal',
      });
    });

  const openCollectionOptionModal = ({
    onSubmit,
    id,
    hideProfileSeqNo,
    targetUrl,
  }: {
    onSubmit?: (param: { text: string; value: string }) => void;
    id: number;
    hideProfileSeqNo: string;
    targetUrl?: string;
  }) => {
    openModal(CollectionOptionModal, {
      type: !isMobile ? 'modal' : 'drawer',
      value: {
        modalTitle: '신고/숨김 설정',
        collectionOption: [
          { text: '신고하기', value: '1' },
          { text: '이 회원의 게시글 숨기기', value: '2' },
        ],
        isDesktop: !isMobile,
        isIPhoneXApp: appInfo.isIPhoneXApp,
      },
      onSubmit: async (data: { text: string; value: string }) => {
        if (assistData.isLogin) {
          if (data.value === '1') {
            linkTo.push({
              url: `/rn/styleclip/report/${id}`,
            });
          } else if (data.value === '2') {
            const noticeResult = isMobile
              ? await openNoticeModal({
                  type: 'confirm',
                  message:
                    '이 회원의 콘텐츠를 모두 숨기시겠습니까?<br>숨김 후에는 다시 취소하실 수 없습니다.',
                  isMobile: isMobile || false,
                })
              : window.confirm(
                  '이 회원의 콘텐츠를 모두 숨기시겠습니까?\n숨김 후에는 다시 취소하실 수 없습니다.'
                );

            if (noticeResult) {
              const result = await requestStyleclipProfileHide({
                hideProfileSeqNo: parseInt(hideProfileSeqNo, 10),
              });
              if (result === 'SUCCESS') {
                alert('숨김 처리되었습니다.');
                linkTo.push({
                  url: '/rn/styleclip',
                });
              }
            }
          }
        } else {
          openLoginConfirm(targetUrl);
        }
      },
    });
  };

  const openMyStyleClipOptionModal = ({
    id,
    targetUrl,
  }: {
    id: number;
    targetUrl?: string;
  }) => {
    openModal(CollectionOptionModal, {
      type: !isMobile ? 'modal' : 'drawer',
      value: {
        modalTitle: '콘텐츠 설정',
        collectionOption: [{ text: '수정하기', value: '1' }],
        isDesktop: !isMobile,
        isIPhoneXApp: appInfo.isIPhoneXApp,
      },
      onSubmit: async (data: { text: string; value: string }) => {
        if (assistData.isLogin) {
          if (data.value === '1') {
            linkTo.push({
              url: `/rn/styleclip/edit/${id}?type=afterApprove`,
            });
          }
        } else {
          openLoginConfirm(targetUrl);
        }
      },
    });
  };
  const openAgreeModal = (options?: {
    onSubmit?: () => void;
    onClose?: () => void;
  }) => {
    // 앱 + 액션바(gnbType) 프로모션: 모달이 네이티브 액션바에 가려지므로
    // 모달 열기 전 액션바를 닫고, 닫힐 때 다시 연다.
    if (appInfo?.isNativeAppBoth && getIsGnbType()) {
      document.location.href = 'wconceptkr://actionBarOff';
    }
    const restoreActionBar = () => {
      if (appInfo?.isNativeAppBoth && getIsGnbType()) {
        document.location.href = 'wconceptkr://actionBarOn';
      }
    };
    openModal(AgreeModal, {
      type: !isMobile ? 'modal' : 'drawer',
      onClose: () => {
        restoreActionBar();
        options?.onClose?.();
      },
      onSubmit: () => {
        restoreActionBar();
        options?.onSubmit?.();
      },
      value: {
        isDesktop: !isMobile,
        isIPhoneXApp: appInfo.isIPhoneXApp,
      },
    });
  };

  // 마케팅 수신동의 모달
  const openMarketingAgreeModal = (): Promise<MarketingAgreeResult | null> =>
    new Promise(resolve => {
      // 앱 + 액션바(gnbType) 프로모션: 모달이 네이티브 액션바에 가려지므로
      // 모달 열기 전 액션바를 닫고, 닫힐 때 다시 연다.
      if (appInfo?.isNativeAppBoth && getIsGnbType()) {
        document.location.href = 'wconceptkr://actionBarOff';
      }
      const restoreActionBar = () => {
        if (appInfo?.isNativeAppBoth && getIsGnbType()) {
          document.location.href = 'wconceptkr://actionBarOn';
        }
      };
      openModal(MarketingAgreeModal, {
        type: !isMobile ? 'modal' : 'drawer',
        onClose: () => {
          restoreActionBar();
          resolve(null);
        },
        onSubmit: (result: MarketingAgreeResult) => {
          restoreActionBar();
          resolve(result);
        },
        value: {
          isDesktop: !isMobile,
          isApp: !!appInfo?.isNativeAppBoth,
          isIPhoneXApp: false,
        },
      });
    });

  const openRelatedPrdModal = ({
    placement,
    className,
    onClose,
    products,
    onClickProduct,
    value,
    isRestore = false,
  }: {
    placement: 'right' | 'bottom';
    className: string;
    onClose: () => void;
    products: Product[];
    onClickProduct?: (product: Product, index: number) => void;
    value: {
      type: string;
    };
    isRestore?: boolean;
  }) => {
    // 복원 모드가 아닐 때만 상태 저장
    if (!isRestore) {
      setRelatedPrdModalState({
        type: 'RelatedPrdModal',
        placement,
        className,
        products,
        value,
      });
    }

    openModal(RelatedPrdModal, {
      placement,
      type: placement
        ? undefined
        : (() => {
            if (isMobile) return 'drawer';
            return 'modal';
          })(),
      onClose: () => {
        // 모달 닫을 때 상태 제거
        clearRelatedPrdModalState();
        onClose();
      },
      className,
      value: {
        isDesktop: !isMobile,
        isIPhoneXApp: appInfo.isIPhoneXApp,
        items: renderProducts({
          products,
          type: 'simple',
          isStatusCd: true,
          isBrandNameKr: true,
          onClickProduct: (product, index) => {
            // 상품 클릭 시 모달 상태 유지하고 상품 상세로 이동
            if (onClickProduct) {
              onClickProduct(product, index);
            }
            // 상품 상세 페이지로 이동
            linkTo.push({
              url: product.landingUrl || '',
            });
          },
        }),
        ...value,
      },
    });
  };

  const openStylelingGuideModal = () => {
    openModal(StylingGuideModal, {
      type: !isMobile ? 'modal' : 'drawer',
      value: {
        isDesktop: !isMobile,
        isIPhoneXApp: appInfo.isIPhoneXApp,
        rs: '276',
      },
    });
  };

  const openLoadingModal = useCallback(() => {
    openModal(LoadingModal, {
      type: 'modal',
      dimClosable: false,
    });
  }, [openModal]);

  const closeLoadingModal = useCallback(() => {
    closeModal(LoadingModal);
  }, [closeModal]);

  return {
    openShareModal,
    openCollectionOptionModal,
    openMyStyleClipOptionModal,
    openAgreeModal,
    openMarketingAgreeModal,
    openRelatedPrdModal,
    openStylelingGuideModal,
    openFoldMenuModal,
    openLoadingModal,
    closeLoadingModal,
    openLoginConfirm,
    openNoticeModal,
    setRelatedPrdModalState,
    clearRelatedPrdModalState,
  };
};

export type { MarketingAgreeResult };

export default useModalManager;
